import { memo, useRef } from 'react';
import styles from './profileLeftCard.module.scss';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import { images } from '../../../constants/images';

export type AccountSubTab = 'info_personal' | 'info_crediticia' | 'documentacion' | 'ajustes';

type ProfileLeftCardProps = {
    user: any;
    imageProfile: string;
    newImageProfile: { path: string; file: any };
    onChangeImage: (e: any) => void;
    activeTab: AccountSubTab;
    onTabChange: (tab: AccountSubTab) => void;
    completionPercent: number;
    imageLoading?: boolean;
};

export const ProgressRing = ({ percent, size = 130 }: { percent: number; size?: number }) => {
    const radius = size * (56 / 130);
    const cx = size / 2;
    const cy = size / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference * (1 - Math.min(percent, 100) / 100);
    return (
        <svg width={size} height={size} className={styles.progressSvg}>
            <circle r={radius} cx={cx} cy={cy} fill="transparent" stroke="#e0e0e0" strokeWidth={4} />
            {percent > 0 && (
                <circle
                    r={radius}
                    cx={cx}
                    cy={cy}
                    fill="transparent"
                    stroke="#22c55e"
                    strokeWidth={4}
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    transform={`rotate(-90 ${cx} ${cy})`}
                    style={{ transition: 'stroke-dashoffset 0.4s ease' }}
                />
            )}
        </svg>
    );
};

const subNavItems: { id: AccountSubTab; label: string; icon: React.ReactNode }[] = [
    { id: 'info_personal', label: 'Info personal', icon: <PersonOutlineIcon fontSize="small" /> },
    { id: 'info_crediticia', label: 'Info crediticia', icon: <ArticleOutlinedIcon fontSize="small" /> },
    { id: 'documentacion', label: 'Documentación', icon: <DescriptionOutlinedIcon fontSize="small" /> },
    { id: 'ajustes', label: 'Ajustes', icon: <SettingsOutlinedIcon fontSize="small" /> },
];

const ProfileLeftCard = ({
    user,
    imageProfile,
    newImageProfile,
    onChangeImage,
    activeTab,
    onTabChange,
    completionPercent,
    imageLoading = false,
}: ProfileLeftCardProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const emailVerified = user?.email_verification?.verified;
    const displayImage = newImageProfile?.path || imageProfile || images.profile;

    return (
        <div className={styles.card}>
            <div className={styles.avatarWrapper}>
                <ProgressRing percent={completionPercent} />
                <div className={styles.avatarCircle}>
                    <img src={displayImage} alt="Foto de perfil" />
                    {imageLoading && <div className={styles.avatarOverlay}><span className={styles.spinner} /></div>}
                </div>
                <button
                    className={styles.uploadBtn}
                    onClick={() => inputRef.current?.click()}
                    type="button"
                    disabled={imageLoading}
                >
                    <FileUploadOutlinedIcon fontSize="small" />
                </button>
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={onChangeImage}
                />
            </div>

            <p className={styles.name}>
                {user?.name} {user?.last_name}
            </p>

            <div className={styles.emailRow}>
                {!emailVerified && <ErrorIcon className={styles.emailWarning} fontSize="small" />}
                <span className={styles.email}>{user?.email}</span>
            </div>

            <nav className={styles.subNav}>
                {subNavItems.map((item) => (
                    <button
                        key={item.id}
                        type="button"
                        className={`${styles.navItem} ${activeTab === item.id ? styles.active : ''}`}
                        onClick={() => onTabChange(item.id)}
                    >
                        <span className={styles.navIcon}>{item.icon}</span>
                        <span className={styles.navLabel}>{item.label}</span>
                    </button>
                ))}
            </nav>
        </div>
    );
};

export default memo(ProfileLeftCard);
